import type {
  EnrichedOrder,
  EnrichedOrderItem,
  OrderStatus,
  OrderSummary,
} from "./types";

export const ORDER_STATUS_LABELS: Record<string, string> = {
  open: "Em andamento",
  concluido: "Concluído",
  carregamento: "Carregamento",
  encerrado: "Encerrado",
  change_requested: "Alteração solicitada",
  cancelled: "Cancelado",
};

export function formatOrderReference(id: string) {
  return `#${id.slice(0, 8).toUpperCase()}`;
}

export function toggleOrderExpansion(current: Set<string>, orderId: string) {
  const next = new Set(current);
  next.has(orderId) ? next.delete(orderId) : next.add(orderId);
  return next;
}

export const ORDER_STATUS_COLORS: Record<string, string> = {
  open: "#d97706",
  concluido: "#64748b",
  carregamento: "#2563eb",
  encerrado: "#16a34a",
  change_requested: "#9333ea",
  cancelled: "var(--login-muted)",
};

export type OrderReturnBadgeStatus = "DECISION" | "SUBSTITUIDO" | "DEVOLVIDO";

export const ORDER_RETURN_BADGES: Record<
  OrderReturnBadgeStatus,
  { label: string; color: string }
> = {
  DECISION: { label: "Decidindo", color: "#9333ea" },
  SUBSTITUIDO: { label: "Substituído", color: "#0f766e" },
  DEVOLVIDO: { label: "Devolvido", color: "#6b7280" },
};

export function getOrderDisplayStatus(order: { status: OrderStatus }): {
  label: string;
  color: string;
  isStale: boolean;
} {
  if (order.status === "encerrado") {
    return { label: "Encerrado", color: "var(--login-accent)", isStale: false };
  }

  return {
    label: ORDER_STATUS_LABELS[order.status] ?? order.status,
    color: ORDER_STATUS_COLORS[order.status] ?? "var(--login-muted)",
    isStale: false,
  };
}

export function getOrderReturnBadge(
  status: EnrichedOrderItem["return_status"],
) {
  if (status === "NONE") return null;
  return ORDER_RETURN_BADGES[status as OrderReturnBadgeStatus] ?? null;
}

export function getOrderTotalPrice(order: EnrichedOrder) {
  return order.items.reduce((sum, item) => sum + getItemMoneyValue(item), 0);
}

export function getItemMoneyValue(item: EnrichedOrderItem) {
  return Number.isFinite(item.charged_price)
    ? item.charged_price
    : item.computed_price;
}

function getFiniteNumber(value: number | null | undefined, fallback: number) {
  return typeof value === "number" && Number.isFinite(value) ? value : fallback;
}

function roundMoney(value: number) {
  return Math.round((value + Number.EPSILON) * 100) / 100;
}

function getExpectedRegularPaidTotal(
  item: EnrichedOrderItem,
  billableQuantity: number,
  bonusQuantity: number,
) {
  const computedPrice = roundMoney(item.computed_price);
  const effectiveQuantity = billableQuantity + bonusQuantity;

  return roundMoney(
    effectiveQuantity > 0
      ? computedPrice * (billableQuantity / effectiveQuantity)
      : computedPrice,
  );
}

export function buildOrderCardDisplay({
  order,
  today,
}: {
  order: EnrichedOrder | OrderSummary;
  today: string;
}) {
  const status = getOrderDisplayStatus(order);

  return {
    ...status,
    isPastDate: order.quotation_date < today,
    canOpenCheckin:
      !!order.checkin_id &&
      (order.status === "concluido" ||
        order.status === "carregamento" ||
        order.status === "encerrado"),
    totalPrice:
      "total_price" in order ? order.total_price : getOrderTotalPrice(order),
  };
}

export function buildOrderItemDisplay({
  item,
  orderStatus,
  isSuper,
  canManageOrders = isSuper,
  isStale,
}: {
  item: EnrichedOrderItem;
  orderStatus: OrderStatus;
  isSuper: boolean;
  canManageOrders?: boolean;
  isStale: boolean;
}) {
  const isAvailable = item.is_available !== false;
  const isReturned = item.return_status === "DEVOLVIDO";
  const bonusQuantity = getFiniteNumber(item.bonus_quantity, 0);
  const billableQuantity = getFiniteNumber(
    item.billable_quantity,
    Math.max(item.quantity - bonusQuantity, 0),
  );
  const chargedPrice = roundMoney(getItemMoneyValue(item));
  const expectedPaidRegularPrice = getExpectedRegularPaidTotal(
    item,
    billableQuantity,
    bonusQuantity,
  );
  const canEdit =
    canManageOrders ||
    (isAvailable &&
      !isReturned &&
      item.return_status !== "DECISION" &&
      !isStale &&
      orderStatus === "open");

  return {
    isAvailable,
    isReturned,
    canEdit,
    hasSpecialPrice:
      item.return_status !== "DEVOLVIDO" &&
      Math.abs(chargedPrice - expectedPaidRegularPrice) > 0.001,
    bonus: {
      hasBonus: bonusQuantity > 0,
      bonusQuantity,
      billableQuantity,
    },
    returnBadge: getOrderReturnBadge(item.return_status),
  };
}

export function getOrderItemMoneyDisplay(item: EnrichedOrderItem) {
  const { billableQuantity, bonusQuantity } = buildOrderItemDisplay({
    item,
    orderStatus: "open",
    isSuper: false,
    isStale: false,
  }).bonus;
  const regularTotal = getExpectedRegularPaidTotal(
    item,
    billableQuantity,
    bonusQuantity,
  );
  const totalPrice = roundMoney(getItemMoneyValue(item));
  const divisor = billableQuantity > 0 ? billableQuantity : item.quantity;
  const fallbackUnitPrice =
    item.unit_type === "kg"
      ? (item.price_per_kg ?? item.price_per_unit)
      : (item.price_per_box ?? item.price_per_unit);
  const unitPrice = roundMoney(
    divisor > 0 ? totalPrice / divisor : fallbackUnitPrice,
  );
  const hasComparison = Math.abs(totalPrice - regularTotal) > 0.001;

  return {
    unitPrice,
    totalPrice,
    comparisonUnitPrice:
      hasComparison && divisor > 0 ? roundMoney(regularTotal / divisor) : null,
    comparisonTotalPrice: hasComparison ? regularTotal : null,
  };
}
